/**
 * Compliance calendar helpers — pure, deterministic, reference-date-injected so
 * behavior is reproducible in tests (no hidden `new Date()`).
 *
 * Powers the unified compliance calendar (Master Plan Argus Phase 1):
 *   - recurrence math: given a frequency + reference date, compute period
 *     labels and period-end dates for generating dated tasks;
 *   - lead-time urgency bucketing for the calendar's red/amber/green signal;
 *   - checklist progress (required → collected → outstanding).
 *
 * Filing-specific offsets (e.g. Form ADV due 90 days after fiscal year-end) are
 * composed by the caller via `addDays(periodEnd, offset)` — this module owns the
 * period math, not firm-specific policy.
 */

export type ComplianceFrequency =
  | "one_time"
  | "monthly"
  | "quarterly"
  | "semi_annual"
  | "annual"
  | "biennial"
  | "on_event";

export type TaskUrgency = "overdue" | "due_soon" | "upcoming" | "scheduled" | "done";

export type ChecklistItemStatus = "outstanding" | "collected" | "not_applicable";

const DAY_MS = 86_400_000;

function toUtc(date: Date | string): Date {
  return typeof date === "string" ? new Date(`${date}T00:00:00Z`) : date;
}

export function iso(date: Date | string): string {
  return toUtc(date).toISOString().slice(0, 10);
}

export function addDays(date: Date | string, days: number): string {
  return iso(new Date(toUtc(date).getTime() + days * DAY_MS));
}

/** End date (inclusive) of the period of the given frequency containing `date`. */
export function periodEnd(frequency: ComplianceFrequency, date: Date | string): string {
  const d = toUtc(date);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth(); // 0-11
  switch (frequency) {
    case "monthly":
      return iso(new Date(Date.UTC(y, m + 1, 0)));
    case "quarterly": {
      const q = Math.floor(m / 3);
      return iso(new Date(Date.UTC(y, q * 3 + 3, 0)));
    }
    case "semi_annual":
      return iso(m < 6 ? new Date(Date.UTC(y, 6, 0)) : new Date(Date.UTC(y, 12, 0)));
    case "annual":
    case "biennial":
    case "one_time":
    case "on_event":
      return iso(new Date(Date.UTC(y, 12, 0)));
  }
}

/** The next period-end strictly after `after`. Returns null for non-recurring. */
export function nextPeriodEnd(frequency: ComplianceFrequency, after: Date | string): string | null {
  if (frequency === "one_time" || frequency === "on_event") return null;
  const current = periodEnd(frequency, after);
  if (current > iso(after)) return current;
  // Step one day past the current period end and recompute.
  const stride = frequency === "biennial" ? 2 : 1;
  let next = periodEnd(frequency, addDays(current, 1));
  if (frequency === "biennial") {
    // Biennial: skip alternate years from the current period end.
    const endYear = toUtc(current).getUTCFullYear();
    next = iso(new Date(Date.UTC(endYear + stride, 12, 0)));
  }
  return next;
}

export interface Period {
  label: string;
  end: string; // ISO date
}

/** Human label for the period ending on `endDate` at the given frequency. */
export function periodLabel(frequency: ComplianceFrequency, endDate: Date | string): string {
  const d = toUtc(endDate);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  switch (frequency) {
    case "monthly":
      return `${MONTHS[m]} ${y}`;
    case "quarterly":
      return `Q${Math.floor(m / 3) + 1} ${y}`;
    case "semi_annual":
      return `${m < 6 ? "H1" : "H2"} ${y}`;
    case "annual":
    case "biennial":
      return `FY${y}`;
    case "one_time":
    case "on_event":
      return "One-time";
  }
}

/** The next `count` upcoming periods (period-end dates) at/after `from`. */
export function upcomingPeriods(
  frequency: ComplianceFrequency,
  from: Date | string,
  count: number,
): Period[] {
  if (frequency === "one_time" || frequency === "on_event") return [];
  const out: Period[] = [];
  // Start with the current period if its end is still in the future, else next.
  let cursor = periodEnd(frequency, from);
  if (cursor < iso(from)) {
    const n = nextPeriodEnd(frequency, from);
    if (!n) return out;
    cursor = n;
  }
  for (let i = 0; i < count; i++) {
    out.push({ label: periodLabel(frequency, cursor), end: cursor });
    const n = nextPeriodEnd(frequency, cursor);
    if (!n) break;
    cursor = n;
  }
  return out;
}

const TERMINAL = new Set(["completed", "waived"]);

/**
 * Lead-time urgency for the calendar signal.
 *   overdue   — past due and not finished
 *   due_soon  — within the requirement's lead window
 *   upcoming  — due within 30 days but outside the lead window
 *   scheduled — further out
 *   done      — terminal status
 */
export function taskUrgency(
  dueDateIso: string | null,
  leadTimeDays: number,
  status: string,
  todayIso: string,
): TaskUrgency {
  if (TERMINAL.has(status)) return "done";
  if (!dueDateIso) return "scheduled";
  if (dueDateIso < todayIso) return "overdue";
  const leadStart = addDays(dueDateIso, -Math.max(0, leadTimeDays));
  if (todayIso >= leadStart) return "due_soon";
  const in30 = addDays(todayIso, 30);
  if (dueDateIso <= in30) return "upcoming";
  return "scheduled";
}

export interface ChecklistProgress {
  total: number;
  applicable: number;
  collected: number;
  outstanding: number;
  notApplicable: number;
  pct: number; // 0-100 of applicable items collected
  complete: boolean;
}

export function checklistProgress(items: { status: ChecklistItemStatus }[]): ChecklistProgress {
  const total = items.length;
  const notApplicable = items.filter((i) => i.status === "not_applicable").length;
  const collected = items.filter((i) => i.status === "collected").length;
  const applicable = total - notApplicable;
  const outstanding = applicable - collected;
  const pct = applicable === 0 ? 100 : Math.round((collected / applicable) * 100);
  return {
    total,
    applicable,
    collected,
    outstanding,
    notApplicable,
    pct,
    complete: applicable > 0 && outstanding === 0,
  };
}

/* ------------------------------------------------------- Code of ethics */

export interface AttestationProgress {
  total: number;
  acknowledged: number;
  pending: number;
  exceptions: number;
  pct: number; // % of roster acknowledged
  complete: boolean;
}

export function attestationProgress(
  rows: { status: "pending" | "acknowledged" | "exception" }[],
): AttestationProgress {
  const total = rows.length;
  const acknowledged = rows.filter((r) => r.status === "acknowledged").length;
  const exceptions = rows.filter((r) => r.status === "exception").length;
  const pending = rows.filter((r) => r.status === "pending").length;
  const pct = total === 0 ? 0 : Math.round((acknowledged / total) * 100);
  return { total, acknowledged, pending, exceptions, pct, complete: total > 0 && pending === 0 };
}

/* --------------------------------------------- Political contributions */

/**
 * Pay-to-play de minimis (SEC Advisers Act Rule 206(4)-5): a covered associate
 * may contribute up to $350 per election to an official they are entitled to
 * vote for, or $150 otherwise, without triggering the two-year ban on advisory
 * fees from associated government-entity clients.
 */
export const DE_MINIMIS_VOTING = 350;
export const DE_MINIMIS_NON_VOTING = 150;

export function deMinimisLimit(canVoteForRecipient: boolean): number {
  return canVoteForRecipient ? DE_MINIMIS_VOTING : DE_MINIMIS_NON_VOTING;
}

export type PayToPlayRisk = "within_de_minimis" | "exceeds_de_minimis";

export interface PayToPlayAssessment {
  limit: number;
  exceeds: boolean;
  risk: PayToPlayRisk;
}

export function assessPayToPlay(
  amountUsd: number,
  canVoteForRecipient: boolean,
): PayToPlayAssessment {
  const limit = deMinimisLimit(canVoteForRecipient);
  const exceeds = amountUsd > limit;
  return { limit, exceeds, risk: exceeds ? "exceeds_de_minimis" : "within_de_minimis" };
}
